'use client'

import { useState } from 'react'
import { CirclePercent, Gift, LoaderCircle, Medal, Minus, Plus, Trash2 } from 'lucide-react'
import { formatPrice } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { useCartItem } from '@/lib/commerce/hooks'

type Pending = null | 'remove' | 'add' | 'sub'

export default function CartItemSidebar({ product, pathname }: { product: any, pathname: string }) {
  const c = useCartItem(product)
  const [pending, setPending] = useState<Pending>(null)
  const [prevLoading, setPrevLoading] = useState(c.loading)
  if (prevLoading !== c.loading) {
    setPrevLoading(c.loading)
    if (!c.loading && pending) setPending(null)
  }
  const run = (kind: Exclude<Pending, null>, fn: () => void) => {
    setPending(kind)
    fn()
  }
  const onRemove = () => run('remove', c.handleRemove)
  const onAdd = () => run('add', c.handleAddOne)
  const onSub = () => run('sub', c.handleRemoveOne)
  const busy = (kind: Exclude<Pending, null>) => pending === kind && c.loading

  return (
    <div className="flex items-start gap-3 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className="relative shrink-0">
        <div
          className="w-20 h-20 rounded-lg overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <img src={c.imageUrl} alt={c.productName} className="w-full h-full object-cover object-center" />
        </div>
        {c.isReward && (
          <div
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full grid place-items-center"
            style={{ background: '#065f46', color: '#a7f3d0', border: '1px solid rgba(16,185,129,0.4)' }}
          >
            <Gift className="w-2.5 h-2.5" strokeWidth={2.5} />
          </div>
        )}
      </a>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className="min-w-0">
            <h3 className="text-[13px] font-medium text-white leading-snug line-clamp-2 wrap-break-word hover:opacity-80 transition-opacity">
              {c.productName}
            </h3>
          </a>
          {c.productData?.isDigital && (
            <span
              className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(96,165,250,0.16)', color: '#93c5fd', border: '1px solid rgba(96,165,250,0.3)' }}
            >
              Дижитал
            </span>
          )}
          {c.showDiscountedPrice && c.discountName && (
            <span
              className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded"
              style={c.isReward
                ? { background: 'rgba(16,185,129,0.16)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,0.3)' }
                : { background: 'rgba(168,85,247,0.16)', color: '#d8b4fe', border: '1px solid rgba(168,85,247,0.3)' }}
            >
              {c.isReward ? <Gift className="w-2.5 h-2.5 mr-1 shrink-0" strokeWidth={2.5} /> : <CirclePercent className="w-2.5 h-2.5 mr-1 shrink-0" strokeWidth={2.5} />}
              <span className="line-clamp-1">{c.discountName}</span>
            </span>
          )}
          {c.loyaltyDeal && (
            <span
              className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded whitespace-nowrap"
              style={{ background: 'rgba(139,92,246,0.16)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)' }}
            >
              <Medal className="w-2.5 h-2.5 mr-1 shrink-0" strokeWidth={2.5} /> Лояалти −{c.loyaltyDeal.percent}%
            </span>
          )}
        </div>

        {c.showVariantName && (
          <div className="flex items-center gap-2 mt-1 text-[11.5px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
            <span>{c.variant?.name}</span>
            {c.dimension && (
              <>
                <span className="w-px h-3" style={{ background: 'rgba(255,255,255,0.2)' }} />
                <span>{c.dimension}</span>
              </>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-2 gap-3">
          <div className="text-[13px]">
            {c.showDiscountedPrice ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-0.5 sm:gap-2">
                <span
                  className="font-semibold tabular-nums"
                  style={{ color: c.unitPrice === 0 ? '#34d399' : '#fda4af' }}
                >
                  {c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}
                </span>
                <span className="text-[11px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {formatPrice(c.qty * (c.originalPrice || 0))}
                </span>
              </div>
            ) : (
              <span className="tabular-nums font-semibold" style={{ color: c.unitPrice === 0 ? '#34d399' : 'white' }}>
                {c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRemove}
              disabled={!!pending}
              className="h-7 w-7 grid place-items-center rounded-md cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {busy('remove') ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" strokeWidth={1.5} />}
            </button>
            {c.isReward ? (
              <div
                className="flex items-center rounded-md px-2 py-1"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <span className="text-[12px] min-w-6 text-center text-white tabular-nums">{c.qty}</span>
              </div>
            ) : c.isOutOfStock ? (
              <div
                className="flex items-center rounded-md px-2 py-1"
                style={{ background: 'rgba(244,63,94,0.16)', border: '1px solid rgba(244,63,94,0.3)' }}
              >
                <span className="text-[10px] font-medium" style={{ color: '#fda4af' }}>Дууссан</span>
              </div>
            ) : (
              <div
                className="flex items-center rounded-full overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <button
                  type="button"
                  onClick={onSub}
                  disabled={!!pending}
                  className="h-7 w-7 grid place-items-center cursor-pointer text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-80"
                >
                  {busy('sub') ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <Minus className="h-3 w-3" />}
                </button>
                <span className="text-[12px] min-w-5 text-center text-white tabular-nums">{c.qty}</span>
                <button
                  type="button"
                  onClick={onAdd}
                  disabled={!!pending}
                  className="h-7 w-7 grid place-items-center cursor-pointer text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-80"
                >
                  {busy('add') ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
